<script setup lang="ts">
import { ref, computed, reactive, watch } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, minLength } from '@vuelidate/validators'
import DialogRevamp from '@/components/DialogRevamp.vue'
import InputFieldFloatingWithMessage from '@/components/ui/input/InputFieldFloatingWithMessage.vue'
import BaseButton from '@/components/ui/button/BaseButton.vue'
import TextButton from '@/components/TextButton.vue'
import BaseAttentionBox from '@/components/ui/attentionbox/BaseAttentionBox.vue'
import useAuthDialog from '@/lib/useAuthDialog'
import injectStrict from '@/lib/inject'
import BaseIcon from '@/components/ui/icon/BaseIcon.vue'

const props = defineProps({
	errorApiMessage: {
		type: String,
		default: '',
	},
})

const emit = defineEmits(['hide', 'backStep', 'toForget', 'toOtp', 'submit'])
const { isShowDialog } = useAuthDialog(hideDialogDirectly)
const textContent: any = injectStrict('textContent')
const formState = reactive({
	password: '',
})
const rules = {
	password: {
		required,
		minLength: minLength(6),
	},
}
const $v = useVuelidate(rules, formState)
const isLoading = ref(false)
const isErrorApiMessageExist = computed(() => props.errorApiMessage !== '')
const passwordErrorMessage = computed(() => {
	if ($v.value.password.required.$invalid) {
		return textContent.input_error_general_required
	}
	if ($v.value.password.minLength.$invalid) {
		return textContent.input_error_password_min_length
	}
	return ''
})

function backStep() {
	emit('backStep')
}

function toForget() {
	emit('toForget')
}

function toOtp() {
	emit('toOtp')
}

function submit() {
	$v.value.$touch()
	if ($v.value.$invalid) {
		return
	}
	isLoading.value = true
	emit('submit', {
		type: 'password',
		password: formState.password,
	})
}

function hideDialogDirectly() {
	emit('hide')
}

watch(
	() => isErrorApiMessageExist.value,
	value => {
		if (value) {
			isLoading.value = false
		}
	},
)
</script>

<template>
	<DialogRevamp
		class="bs-auth-login"
		v-model="isShowDialog"
		:isFullInMobile="true"
		:isCustomOverlayFunction="true"
		:isShowDefaultClose="false"
		:size="'sm'"
		:isSecondLayer="true"
		@overlayClick="backStep"
	>
		<template #header>
			<div class="bs-auth-back-header">
				<button class="bs-icon bs-auth-back-header__back" @click="backStep">
					<BaseIcon :name="'back'" class="h-16px w-16px fill-n-950" />
				</button>
				<h5 class="bs-auth__title">
					{{ textContent.login_enter_password_title }}
				</h5>
			</div>
		</template>
		<template #content>
			<div class="bs-auth-login__content">
				<BaseAttentionBox
					v-if="isErrorApiMessageExist"
					class="bs-auth__attention-box"
					:icon="'error'"
					:background="'error'"
				>
					<template #description>
						{{ errorApiMessage }}
					</template>
				</BaseAttentionBox>
				<InputFieldFloatingWithMessage
					class="bs-auth-login__content__password"
					v-model="$v.password.$model"
					type="password"
					:label="textContent.input_label_password"
					:isError="$v.password.$error"
					:disabled="isLoading"
					:errorMessage="passwordErrorMessage"
				></InputFieldFloatingWithMessage>

				<TextButton
					:text="textContent.login_forgot_password"
					@onClick="toForget"
				></TextButton>
			</div>
		</template>
		<template #footer>
			<BaseButton
				:size="'large'"
				:isFull="true"
				:isLoading="isLoading"
				:isDisabled="isLoading"
				class="bs-auth-login__footer__submit"
				@click="submit"
			>
				{{ textContent.button_login }}
			</BaseButton>

			<div class="bs-auth-login__footer__forget">
				<TextButton
					:text="textContent.login_switch_to_one_time_password"
					:type="'secondary'"
					@onClick="toOtp"
				></TextButton>
			</div>
		</template>
	</DialogRevamp>
</template>
